/**
 * Theme value object — pure types, no logic.
 * No external dependencies allowed.
 */

export type ButtonPosition =
    | "bottom-right"
    | "bottom-left"
    | "top-right"
    | "top-left";
export type ButtonSize = "small" | "medium" | "large";
export type SpaceLevel = "1" | "2" | "3" | "4" | "5";

/**
 * Controls how the chat window is presented.
 * - `popup`      — floating card near the launcher button (default)
 * - `side-panel` — full-height drawer docked to the viewport edge
 * - `fullscreen` — covers the entire viewport
 * - `inline`     — positioned statically inside its parent container
 */
export type WindowMode = "popup" | "side-panel" | "fullscreen" | "inline";

/** Avatar configuration for bot and user sides. */
export interface AvatarConfig {
    /** URL for the bot avatar image. Omit to use the default robot SVG. */
    bot?: string;
    /** URL for the user avatar image. Omit to use the default person SVG. */
    user?: string;
    /** URL for the header avatar image. Omit to use the default robot SVG. */
    header?: string;
    /** Show bot avatar. Default: true. */
    showBot?: boolean;
    /** Show user avatar. Default: true. */
    showUser?: boolean;
}

export interface ThemeColors {
    primary: string;
    secondary: string;
    background: string;
    text: string;
    border: string;
}

export interface LayoutConfig {
    width?: string;
    height?: string;
    maxWidth?: string;
    maxHeight?: string;
    horizontalSpace?: SpaceLevel;
    verticalSpace?: SpaceLevel;
}

/**
 * End-of-conversation survey configuration.
 * The connector must implement `sendSurvey` for submissions to be delivered.
 */
export interface EndOfConversationSurveyConfig {
    /** Enable the survey flow. Default: `true`. */
    enabled?: boolean;
    /**
     * Where the survey is shown.
     * - `"inline"` — appended to the message list as a bot-authored card
     * - `"screen"` — full-height overlay over the chat view (default)
     */
    mode?: "inline" | "screen";
    /**
     * When to trigger the survey.
     * - `"onClose"` — intercept the close button and show the survey first (default)
     * - `"manual"`  — only shown when the host dispatches `show-survey`
     */
    trigger?: "onClose" | "manual";
    /** Maximum rating value (stars). Default: `5`. */
    maxRating?: number;
    /**
     * If the user selects a rating at or below this value, a comment is required
     * before submit is enabled. Set to `0` to never require a comment.
     * Default: `3`.
     */
    requireCommentBelow?: number;
    /**
     * Opaque identifier forwarded to the connector as `SurveyPayload.kind`.
     * Useful when the backend distinguishes bot-vs-agent surveys.
     * Default: `1`.
     */
    kind?: string | number;
    /**
     * After submit or skip, fully tear down the engine and connector so the
     * next open starts from scratch (fresh connection, empty history, launcher
     * view). Set to `false` to keep the session alive and just close the
     * widget (messages, connection, and state are preserved).
     * Default: `true`.
     */
    resetOnSubmit?: boolean;
}

export interface ThemeConfig {
    allowFullscreen?: boolean;
    colors: ThemeColors;
    position: ButtonPosition;
    positionMargin?: SpaceLevel;
    size: ButtonSize;
    layout: LayoutConfig;
    /** Avatar configuration for bot and user sides. */
    avatar?: AvatarConfig;
    /** Show delivery/read status ticks on user messages. Default: false. */
    showMessageStatus?: boolean;
    /** Enable the search toggle button in the chat header. Default: true. */
    enableSearch?: boolean;
    /**
     * How the chat window is presented. Default: `"popup"`.
     * See `WindowMode` for details.
     */
    windowMode?: WindowMode;
    /**
     * Enable multi-conversation mode inside the popup.
     * When `true`, a conversations icon button appears in the chat header.
     * Clicking it opens a conversation list view (slide-in within the popup).
     * Requires the connector to implement `listConversations` / `switchConversation`.
     * Default: `false`.
     */
    enableMultiConversation?: boolean;
    /** Enable the file upload button in the chat input. Default: true. */
    enableFileUpload?: boolean;
    /**
     * Hide the launcher button when the chat window is open.
     * Only applies when slotted (custom) content is used.
     * Default: `false`.
     */
    hideButtonOnOpen?: boolean;
    /** End-of-conversation survey configuration. Default: disabled. */
    endOfConversationSurvey?: EndOfConversationSurveyConfig;
}

export const DEFAULT_THEME: ThemeConfig = {
    colors: {
        primary: "#4f46e5",
        secondary: "#6c757d",
        background: "#ffffff",
        text: "#212529",
        border: "#dee2e6",
    },
    position: "bottom-right",
    positionMargin: "2",
    size: "medium",
    layout: {
        width: "360px",
        height: "520px",
        maxWidth: "100%",
        maxHeight: "100%",
        horizontalSpace: "2",
        verticalSpace: "2",
    },
    showMessageStatus: true,
    endOfConversationSurvey: {
        enabled: true,
        mode: "screen",
        trigger: "onClose",
        maxRating: 5,
        requireCommentBelow: 3,
        kind: 1,
        resetOnSubmit: true,
    },
};

/** Build CSS variable map from a ThemeConfig. */
export function themeToCSS(theme: ThemeConfig): Record<string, string> {
    return {
        "--chativa-primary-color": theme.colors.primary,
        "--chativa-secondary-color": theme.colors.secondary,
        "--chativa-background-color": theme.colors.background,
        "--chativa-text-color": theme.colors.text,
        "--chativa-border-color": theme.colors.border,
    };
}

/** Deep merge a partial theme over a base theme. */
export function mergeTheme(
    base: ThemeConfig,
    overrides: DeepPartial<ThemeConfig>,
): ThemeConfig {
    return {
        ...base,
        ...overrides,
        colors: { ...base.colors, ...(overrides.colors ?? {}) },
        layout: { ...base.layout, ...(overrides.layout ?? {}) },
        endOfConversationSurvey:
            base.endOfConversationSurvey || overrides.endOfConversationSurvey
                ? {
                      ...(base.endOfConversationSurvey ?? {}),
                      ...(overrides.endOfConversationSurvey ?? {}),
                  }
                : undefined,
    } as ThemeConfig;
}

export type DeepPartial<T> = {
    [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
